import { Box, Stack, StackProps } from "@mui/material";
import React from "react";
import { color } from "../../../../common/MuiThemeProvider.js";

interface PendingChangeValueProps {
  // The OLD value being replaced. When present, rendered ON TOP with `text-decoration:
  // line-through` (no background). Pass `undefined` when there is no diff (no pending
  // change) — in that case only `changedValue` renders.
  value?: React.ReactNode;
  // The canonical / new / actual value. Always rendered ON BOTTOM. When `value` (OLD)
  // is also present, this slot gets a yellow background to signal "this is the NEW value
  // that replaced the OLD". When there is no OLD, this slot renders plain (no highlight).
  // This is the slot that becomes editable in edit mode.
  changedValue: React.ReactNode;
  // Underlying element used for the outer Stack and inner Boxes. Use "span" inside
  // inline contexts (e.g. Typography); defaults to "div" for block layouts.
  component?: React.ElementType;
}

export function PendingChangeValue({
  value,
  changedValue,
  component = "div",
  sx,
  ...props
}: PendingChangeValueProps & StackProps) {
  const hasOld = value != null;
  return (
    <Stack
      component={component}
      direction="column"
      useFlexGap
      {...props}
      sx={[
        component === "span" && { display: "inline-flex" },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {hasOld ? (
        <Box component={component} sx={{ textDecoration: "line-through" }}>
          {value}
        </Box>
      ) : null}
      <Box
        component={component}
        sx={
          hasOld
            ? {
                backgroundColor: color.yellowLight,
                borderRadius: "2px",
                padding: "0 4px",
                margin: "0 -4px",
              }
            : undefined
        }
      >
        {changedValue}
      </Box>
    </Stack>
  );
}

interface PreviewValueProps {
  value: string | number | undefined;
  changedValue: string | number | undefined;
  prefix?: string;
  suffix?: string;
}

export function PreviewValue({
  value,
  changedValue,
  prefix = "",
  suffix = "",
  ...props
}: PreviewValueProps & StackProps) {
  // Strict undefined check — `Boolean(changedValue)` would treat `0` and `""`
  // as "no change", hiding the pending overlay for legitimate zero values
  // (quantity / unit / time pending changes can be 0).
  const hasChanged = changedValue !== undefined && changedValue !== value;
  return (
    <PendingChangeValue
      component="span"
      value={
        // Only populate the OLD slot when there is an actual old value.
        // `hasChanged` can be true while `value` is undefined (a defined
        // `changedValue` differing from an undefined `value`); passing a
        // fragment then makes `PendingChangeValue` treat it as an old value
        // and render an empty strikethrough line + highlight.
        hasChanged && value !== undefined ? (
          <>
            {prefix}
            {value}
            {suffix}
          </>
        ) : undefined
      }
      changedValue={
        <>
          {prefix}
          {hasChanged ? changedValue : value}
          {suffix}
        </>
      }
      {...props}
    />
  );
}
